import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import * as api from "../lib/api.ts";
import { SessionChoices } from "./SessionChoices.tsx";
import { Insights } from "./Insight.tsx";
import { weekDoneLabel } from "../lib/home.ts";

/**
 * Starting a session is an action, not a place: a sheet over the screen one
 * is on, rather than a tab to land on. Closing it leaves that screen as it
 * was.
 */
export function StartSheet({ onClose }: { onClose: () => void }) {
  const cycle = useQuery({ queryKey: ["cycle"], queryFn: api.getCycle });
  const data = cycle.data?.cycle ?? null;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Into the body, clear of the navigation's own stacking.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Démarrer une séance"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 md:items-center"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="max-h-[85dvh] w-full max-w-3xl overflow-y-auto rounded-t-card bg-base px-4 pt-4 safe-bottom md:rounded-card md:px-6 md:pb-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {data && (
              <p className="label">
                {data.name} · semaine {data.currentWeek}/{data.weeks} ·{" "}
                {weekDoneLabel(data.doneThisWeek, data.templates.length)}
              </p>
            )}
            <h2 className="wide mt-1 text-[1.4rem] font-bold">Démarrer une séance</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink-mute active:bg-surface-high"
          >
            <X size={20} aria-hidden />
          </button>
        </div>

        <Insights scope="start" className="mt-3" />

        <div className="mt-4">
          {cycle.isLoading ? (
            <p className="py-6 text-ink-mute">Chargement…</p>
          ) : data && data.templates.length > 0 ? (
            <SessionChoices cycle={data} onStarted={onClose} />
          ) : (
            <p className="py-6 text-[0.95rem] text-ink-soft">
              Aucun programme importé : les séances arrivent de la feuille du coach, dans Réglages.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
